import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ClockIcon, GiftIcon } from 'lucide-react';
import { useProto } from '../../contexts/PrototypeContext';
import { formatCountdown, formatMoney } from '../../utils/format';
import { Annotation } from '../shared/Annotation';
const ease = [0.23, 1, 0.32, 1] as const;
export function CartSummary({
  device
}: {
  device: 'mobile' | 'desktop';
}) {
  const {
    state,
    cart,
    discountApplied,
    holdRemainingMs,
    actions
  } = useProto();
  const volunteerName = state.choice === 'someone' ? `${state.someone.firstName} ${state.someone.lastName}`.trim() || 'someone else' : 'you';
  return <aside aria-labelledby="summary-title" className={`rounded-lg border border-i9-line bg-i9-surface p-5 font-i9 ${device === 'desktop' ? 'sticky top-6' : ''}`} data-id="element-224" data-mp="1esxyt35ss9qp">
      <h2 id="summary-title" className="text-[17px] font-bold text-i9-ink" data-id="element-225" data-mp="m5k83msp0af1">Order summary</h2>
      <dl className="mt-4 space-y-2.5 text-[14px]" data-id="element-226" data-mp="r4eq4miu5uy3">
        <div className="flex justify-between gap-3" data-id="element-227" data-mp="1yvnmaugxwpfr">
          <dt className="text-i9-muted" data-id="element-228" data-mp="1yuc9y69fi9d1">Subtotal ({cart.items.length} registrations)</dt>
          <dd className="tabular-nums text-i9-ink" data-id="element-229" data-mp="1txlthhtppbr3">{formatMoney(cart.subtotal)}</dd>
        </div>
        {cart.adjustments.map(a => <div key={a.id} className="flex justify-between gap-3" data-id="element-230" data-mp="1p3rayhpuf2tl">
            <dt className="text-i9-muted" data-id="element-231" data-mp="1l3h8hf19eujh">
              {a.label} <span className="text-[12px]" data-id="element-232" data-mp="1g55tkpw3xtue">· {a.detail}</span>
            </dt>
            <dd className="tabular-nums text-i9-ink" data-id="element-233" data-mp="1g32znuru3gke">{formatMoney(a.amount)}</dd>
          </div>)}
        <AnimatePresence initial={false} data-id="element-234" data-mp="1b8fhednfbie9">
          {cart.volunteer && <motion.div key="vrd" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.22,
          ease
        }} className="overflow-hidden" data-id="element-235" data-mp="1679p2gxodx59">
              <div className="flex items-start justify-between gap-3 rounded-md border border-i9-greenLine/70 bg-i9-green px-3 py-2.5" data-id="element-236" data-mp="2whb7ej7y3lbz">
                <dt className="flex gap-2 text-i9-greenInk" data-id="element-237" data-mp="yo0dqi5lzodk">
                  <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-i9-greenIcon" aria-hidden="true" data-id="element-238" data-mp="ypjwqgingdj1" />
                  <span data-id="element-239" data-mp="ktzelnaqvzco">
                    <span className="block font-bold" data-id="element-240" data-mp="oxe3l3gnvq29">{cart.volunteer.label}</span>
                    <span className="block text-[12px]" data-id="element-241" data-mp="ovb843wqp4q5">Volunteer: {volunteerName} · 1 per program</span>
                  </span>
                </dt>
                <dd className="font-bold tabular-nums text-i9-greenInk" data-id="element-242" data-mp="1hl9v7trdirs6">{formatMoney(cart.volunteer.amount)}</dd>
              </div>
            </motion.div>}
        </AnimatePresence>
      </dl>

      <div className="mt-4 flex items-baseline justify-between border-t border-i9-line pt-4" data-id="element-243" data-mp="18kbrzdoreoan">
        <span className="font-bold text-i9-ink" data-id="element-244" data-mp="3obrdllnatjkd">Total due today</span>
        <motion.span key={cart.total} initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="text-[24px] font-extrabold tabular-nums text-i9-ink" aria-live="polite" data-id="element-245" data-mp="3tcez5rsrd877">
          {formatMoney(cart.total)}
        </motion.span>
      </div>

      {discountApplied && <div className="mt-4 space-y-2" data-id="element-246" data-mp="3pu7rqn5jzznt">
          <p className="flex items-start gap-2 rounded-md border border-i9-line bg-white px-3 py-2.5 text-[12.5px] text-i9-muted" data-id="element-247" data-mp="3pv9dblk744js">
            <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-248" data-mp="322aohkg66fyb" />
            <span data-id="element-249" data-mp="2x6vdd09jqy5l">
              Discount reserved for <span className="font-bold tabular-nums text-i9-ink" data-id="element-250" data-mp="3btwzpgsicf12">{formatCountdown(holdRemainingMs)}</span>. Complete checkout to keep it.
            </span>
          </p>
          <Annotation kind="placeholder" title="Decision #1 · 30-minute hold is not approved" data-id="element-251" data-mp="371dlchlzxfv7">
            Reservation duration isn't finalized, and whether customers should see a countdown is an open question.
          </Annotation>
          <Annotation kind="unresolved" title="Decision #3 · reservation & cart persistence" data-id="element-252" data-mp="1hnnydxsx8qh5">
            Undefined: what happens to this hold if the customer closes the browser, signs out, returns to an existing cart, or switches devices before it expires.
          </Annotation>
        </div>}

      <button type="button" onClick={actions.checkout} className="mt-5 w-full bg-i9-steel py-3.5 text-[16px] font-bold text-white shadow-md transition-colors duration-150 hover:bg-i9-steelDark" data-id="element-253" data-mp="1vfcc25itjn08">
        Next: Payment
      </button>
      <button type="button" onClick={() => actions.goToStep('volunteer')} className="mt-2 w-full py-2.5 text-[14px] font-semibold text-i9-steel transition-colors hover:text-i9-steelDark" data-id="element-254" data-mp="25f36663gvzzs">
        Back to Volunteer Info
      </button>
    </aside>;
}